import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../lib/formatDate';
import { errorMessage, toastError, toastSuccess } from '../../lib/toast';
import {
  cancelAccessRequest, cancelProposal, listAccessRequests, listOf, listProposals,
} from './vendorsApi';
import {
  LEVEL_LABEL, PROPOSAL_KIND_LABEL, PROPOSAL_STATUS_LABEL, VENDOR_ERRORS,
} from './vendorLabels';
import { Skeleton, FullError, ProposalOutcome } from './VendorBits';

const statusClass = (s) => {
  if (s === 'approved') return 'vd-chip--ok';
  if (s === 'pending') return '';
  return 'vd-chip--bad';
};

function Row({ title, meta, status, onCancel, outcome = null }) {
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try { await onCancel(); } finally { setBusy(false); }
  };
  return (
    <li>
      <div className="vd-card-head">
        <strong>{title}</strong>
        <span className={`vd-chip ${statusClass(status)}`}>{PROPOSAL_STATUS_LABEL[status] || status}</span>
      </div>
      <div className="vd-list-meta">{meta}</div>
      {outcome}
      {status === 'pending' && (
        <div className="vd-card-actions">
          <button type="button" className="btn btn-secondary btn-small" onClick={cancel} disabled={busy}>Cancel</button>
        </div>
      )}
    </li>
  );
}

/** The caller's own proposals and access requests, with Cancel while pending. */
export default function MyVendorRequests() {
  const [state, setState] = useState({ status: 'loading', proposals: [], requests: [] });

  const load = useCallback(() => {
    setState((s) => ({ ...s, status: 'loading' }));
    Promise.all([listProposals('mine'), listAccessRequests('mine')])
      .then(([p, r]) => setState({ status: 'ready', proposals: listOf(p, 'proposals'), requests: listOf(r, 'requests') }))
      .catch(() => setState({ status: 'error', proposals: [], requests: [] }));
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = (fn, id) => async () => {
    try {
      await fn(id);
      toastSuccess('Cancelled.');
    } catch (err) {
      toastError(errorMessage(err, 'Could not cancel the request.', VENDOR_ERRORS));
    }
    load();
  };

  if (state.status === 'loading') return <Skeleton label="Loading your requests" />;
  if (state.status === 'error') return <FullError message="Couldn't load your requests. Try again in a minute." onRetry={load} />;
  if (!state.proposals.length && !state.requests.length) {
    return <div className="vd-empty"><p>You haven&apos;t made any vendor requests yet.</p></div>;
  }

  return (
    <div>
      {state.proposals.length > 0 && (
        <section aria-labelledby="vd-my-proposals">
          <h2 id="vd-my-proposals" className="vd-group-head">Change proposals</h2>
          <ul className="vd-list">
            {state.proposals.map((p) => (
              <Row
                key={p.id}
                status={p.status}
                onCancel={cancel(cancelProposal, p.id)}
                outcome={<ProposalOutcome proposal={p} />}
                title={p.vendor ? <Link to={`/vendors/${p.vendor.id}`}>{p.vendor.name}</Link> : `New vendor ${p.changes?.name?.to ?? ''}`.trim()}
                meta={`${PROPOSAL_KIND_LABEL[p.kind] || p.kind} · ${formatDate(p.created_at)}`}
              />
            ))}
          </ul>
        </section>
      )}
      {state.requests.length > 0 && (
        <section aria-labelledby="vd-my-requests">
          <h2 id="vd-my-requests" className="vd-group-head">Access requests</h2>
          <ul className="vd-list">
            {state.requests.map((r) => (
              <Row
                key={r.id}
                status={r.status}
                onCancel={cancel(cancelAccessRequest, r.id)}
                title={r.tag?.name || r.tag_name}
                meta={`${LEVEL_LABEL[r.level] || r.level} · ${formatDate(r.created_at)}`}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
